import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Sparkles,
  Target,
  Zap,
  Shield,
  FileText,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Star,
  Users,
  Download
} from 'lucide-react'
import Button from '../components/common/Button'

function Home() {
  const features = [
    {
      icon: Sparkles,
      title: 'AI-Powered Resume Generation',
      description: 'Transform your LinkedIn profile into a professional resume in seconds with advanced AI technology.',
    },
    {
      icon: Target,
      title: 'Job-Matched Resumes',
      description: 'Create tailored resumes that match specific job descriptions, highlighting relevant skills and experience.',
    },
    {
      icon: Shield,
      title: 'ATS Optimization',
      description: 'Get your resume past Applicant Tracking Systems with our ATS score checker and optimization tools.',
    },
    {
      icon: FileText,
      title: 'Professional Templates',
      description: 'Choose from 10+ premium resume templates designed by HR professionals and career experts.',
    },
    {
      icon: Zap,
      title: 'One-Click Export',
      description: 'Export your resume as PDF or DOCX with a single click. Ready to apply immediately.',
    },
    {
      icon: TrendingUp,
      title: 'Profile Score & Analytics',
      description: 'Get actionable insights on your LinkedIn profile and resume with detailed scoring and recommendations.',
    },
  ]

  const benefits = [
    'Save 10+ hours per job application',
    'Increase interview callbacks by 3x',
    'Beat ATS filters with 95% success rate',
    'Generate unlimited resumes',
    'No design skills required',
    'Privacy-focused - your data stays secure',
  ]

  const stats = [
    { value: '50K+', label: 'Active Users' },
    { value: '200K+', label: 'Resumes Generated' },
    { value: '4.8/5', label: 'User Rating' },
    { value: '95%', label: 'ATS Pass Rate' },
  ]

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <>
      <Helmet>
        <title>HireFlow - Transform LinkedIn to Professional Resume with AI</title>
        <meta
          name="description"
          content="Create ATS-optimized resumes from your LinkedIn profile in seconds. AI-powered resume builder with job-matching technology. Join 50,000+ users landing their dream jobs."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-primary-50 -z-10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(14,165,233,0.1),transparent_50%)] -z-10" />

        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
                <Star className="w-4 h-4 text-primary-600 fill-current" />
                <span className="text-sm font-medium text-primary-700">
                  Trusted by 50,000+ professionals
                </span>
              </div>

              <h1 className="heading-xl mb-6">
                Transform Your{' '}
                <span className="gradient-text">LinkedIn Profile</span>
                <br />
                into a Professional Resume
              </h1>

              <p className="text-xl text-neutral-600 mb-8 max-w-2xl mx-auto">
                Create ATS-optimized, job-matched resumes in seconds with AI.
                Land more interviews and get hired faster.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  variant="primary"
                  size="lg"
                  href="https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob"
                  external
                  className="shadow-lg hover:shadow-xl"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Install Free Extension
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  href="/pricing"
                >
                  View Pricing
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>

              <p className="text-sm text-neutral-500 mt-4">
                Free forever. No credit card required.
              </p>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-16 lg:mt-20"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-neutral-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <h2 className="heading-lg mb-4">
                Everything You Need to{' '}
                <span className="gradient-text">Land Your Dream Job</span>
              </h2>
              <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
                Powerful features designed to help you stand out and get hired faster
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="card p-6 h-full hover:border-primary-200 transition-colors">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <h2 className="heading-lg mb-6">
                Why Choose{' '}
                <span className="gradient-text">HireFlow</span>?
              </h2>
              <p className="text-lg text-neutral-600 mb-8">
                Join thousands of professionals who have accelerated their job search
                with HireFlow's intelligent resume builder.
              </p>

              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8">
                <Button
                  variant="primary"
                  size="lg"
                  href="https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob"
                  external
                >
                  Get Started Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="card p-8 bg-gradient-to-br from-primary-50 to-white">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-neutral-900">50,000+</div>
                      <div className="text-sm text-neutral-600">Active Users</div>
                    </div>
                  </div>

                  <div className="border-t border-neutral-200 pt-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-600">Success Rate</span>
                        <span className="font-semibold text-neutral-900">95%</span>
                      </div>
                      <div className="w-full bg-neutral-200 rounded-full h-2">
                        <div className="bg-primary-600 h-2 rounded-full" style={{ width: '95%' }} />
                      </div>
                    </div>

                    <div className="mt-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-600">Interview Callback Rate</span>
                        <span className="font-semibold text-neutral-900">3x Higher</span>
                      </div>
                      <div className="w-full bg-neutral-200 rounded-full h-2">
                        <div className="bg-primary-600 h-2 rounded-full" style={{ width: '85%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-primary-500">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="heading-lg text-white mb-6">
              Ready to Land Your Dream Job?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Join 50,000+ professionals using HireFlow to create winning resumes
              and accelerate their career growth.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="secondary"
                size="lg"
                href="https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob"
                external
                className="shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5 mr-2" />
                Install Free Extension
              </Button>
              <Button
                variant="outline"
                size="lg"
                href="/pricing"
                className="border-white text-white hover:bg-white hover:text-primary-600"
              >
                View Pricing Plans
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default Home
